/**
 * Página de Información Organizativa - Portal de Transparencia
 * Estilo Awwwards Interior - Estructura organizativa de INTERCASA, S.A.
 */

import React, { useRef, useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import LocalizedLink from '../../components/common/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Información Organizativa con estilo Awwwards
 * @returns {JSX.Element} Página de información organizativa
 */
const OrganizationalInfoPage = () => {
  const { t } = useTranslation(['transparencyDirectors', 'transparency']);
  const heroRef = useRef(null);
  const location = useLocation();

  /**
   * Efecto para hacer scroll al inicio de la página al cargar
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Portal Transparencia */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />
        
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <LocalizedLink 
              to="/transparencia"
              className="group inline-flex items-center text-white/60 hover:text-white mb-8 transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="text-sm font-medium tracking-wide">{t('transparency:backToPortal')}</span>
            </LocalizedLink>
            
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] mb-6 drop-shadow-lg">
              <Trans i18nKey="hero.title" ns="transparencyDirectors" components={{ hl: <span className="text-intercasa-yellow" /> }} />
            </h1>
            
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">

            {/* Información de Administradores y Directivos */}
            <AnimatedSection animation="fadeUp" delay={0.1} className="mb-16">
              <span className="font-lato text-intercasa-red text-sm uppercase tracking-widest mb-4 block">
                {t('label')}
              </span>
              <h2 className="font-baskerville font-bold text-intercasa-dark text-3xl md:text-4xl mb-8">
                {t('title')}
              </h2>
            
              <div className="space-y-6">
                {/* Guillermo Serrano Pérez */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-intercasa-dark rounded-xl flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                      GS
                    </div>
                    <div>
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark">
                        D. Guillermo Serrano Pérez
                      </h3>
                      <p className="text-sm text-intercasa-red font-medium">
                        {t('role')}
                      </p>
                      <p className="text-xs text-intercasa-gray mt-1">
                        {t('serrano.representing')}
                      </p>
                    </div>
                  </div>
                
                  <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                    <p>
                      {t('serrano.p1')}
                    </p>
                    <p>
                      {t('serrano.p2')}
                    </p>
                    <p>
                      {t('serrano.p3')}
                    </p>
                    <p>
                      {t('serrano.p4')}
                    </p>
                    <div>
                      <p className="text-intercasa-dark font-medium mb-3">{t('serrano.milestonesIntro')}</p>
                      <ul className="space-y-3 ml-4">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="serrano.consultant" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="serrano.manager" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                      </ul>
                    </div>
                    <p>
                      {t('serrano.closing')}
                    </p>
                  </div>
                </div>
              
                {/* Yonay José Alemán García */}
                <div className="bg-intercasa-bone/30 rounded-2xl p-6 md:p-8 border border-intercasa-bone">
                  <div className="flex items-start mb-6">
                    <div className="w-16 h-16 bg-intercasa-dark rounded-xl flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                      YA
                    </div>
                    <div>
                      <h3 className="font-baskerville text-xl font-bold text-intercasa-dark">
                        D. Yonay José Alemán García
                      </h3>
                      <p className="text-sm text-intercasa-red font-medium">
                        {t('role')}
                      </p>
                    </div>
                  </div>
                
                  <div className="space-y-4 text-intercasa-dark/70 leading-relaxed">
                    <p>
                      {t('aleman.p1')}
                    </p>
                    <p>
                      {t('aleman.p2')}
                    </p>
                    <div>
                      <p className="text-intercasa-dark font-medium mb-3">{t('aleman.experienceIntro')}</p>
                      <ul className="space-y-3 ml-4">
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="aleman.controller" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="aleman.financialDirector" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="aleman.hotelDirector" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                        <li className="flex items-start">
                          <span className="w-2 h-2 bg-intercasa-red rounded-full mt-2 mr-3 flex-shrink-0"></span>
                          <span><Trans i18nKey="aleman.generalDirector" ns="transparencyDirectors" components={{ bold: <strong /> }} /></span>
                        </li>
                      </ul>
                    </div>
                    <p>
                      {t('aleman.closing')}
                    </p>
                  </div>
                </div>

                {/* Retribución */}
                <div className="bg-intercasa-dark rounded-2xl p-6 md:p-8 text-white">
                  <h4 className="font-baskerville text-lg font-bold mb-3 text-intercasa-yellow">
                    {t('remuneration.title')}
                  </h4>
                  <p className="text-white/70">
                    {t('remuneration.text')}
                  </p>
                </div>
              </div>

              {/* Botones de descarga */}
              <div className="flex flex-wrap gap-4 justify-center mt-6">
                <a 
                  href="/PortalTansparenciaIntercasa/InformacionAdmin/InformacionAdministradoresyDirectivos.pdf" 
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-red text-white rounded-lg hover:bg-intercasa-red/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('transparency:download.pdf')}
                </a>
                <a
                  href="/PortalTansparenciaIntercasa/InformacionAdmin/InformacionAdministradoresyDirectivos.odt"
                  download
                  className="inline-flex items-center px-6 py-3 bg-intercasa-dark text-white rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300 font-lato font-medium"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  {t('transparency:download.odt')}
                </a>
              </div>
            </AnimatedSection>

          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default OrganizationalInfoPage;
